import { DateTime } from '@ksyos/date-time';
import { Text } from '@ksyos/design-system';
import { Map } from 'immutable';
import type { IBreadcrumbItem } from '@frontend/components/elements/common';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { IService } from '@frontend/components/interfaces/service';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { EmbloomGroupCertificate } from '@common/enums/embloomGroupCertificate';
import { ServiceCode } from '@common/enums/serviceCode';
import { ServiceType } from '@common/enums/serviceType';
import { getLanguage } from '@common/translate/translate';
import { formatString } from '@common/utils/formatString';
import { serviceGroupSettingsAddSchema, serviceGroupSettingsEditSchema } from '@common/validation/serviceGroupSettings';
import {
    Button,
    CancelButton,
    DateInput,
    Dropdown,
    DropdownSearch,
    EnumDropdown,
    FormContainer,
    FormGroup,
    FormLabel,
    Header,
    InputCheckbox,
    InputContainer,
    InputGroup,
    InputNumber,
    Loading,
    OverlapMessage,
} from '@frontend/components/elements/common';
import { Component } from '@frontend/components/elements/Component';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { FixedWidthTemplate } from '@frontend/components/templates/FixedWidthTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { hasOneAuthorization } from '@frontend/utils';
import { OverlapChecker } from '@frontend/utils/overlapChecker';
import { RateLimiter } from '@frontend/utils/rateLimiter';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import { CompensationPayoutList } from '../compensationPayouts/CompensationPayoutList';
import { FundingSettingsList } from '../fundingSettings/FundingSettingsList';
import { ServiceSupervisorListAddEdit } from './ServiceSupervisorListAddEdit';

type ServiceGroupSettingsAddEditProps = IWithRouterProps;

interface IServiceGroupSettings {
    embloomGroupCertificate: EmbloomGroupCertificate | null;
    endDate: string | null;
    groupId: string | null;
    isKetenDbc: boolean;
    noShowsImported: boolean;
    serviceId: string | null;
    startDate: string | null;
    supervisionPercentage: number | null;
}

interface IGroup {
    id: string;
    name: string;
}

export interface IServiceSupervisor {
    employment: {
        organization: {
            name: string;
        };
        practitioner: {
            isActivated: boolean;
            nameFamily: string;
            nameGiven: string | null;
            nameInfix: string | null;
        };
    };
    id: string;
}

export type SelectedServiceSupervisors = Map<string, IServiceSupervisor>;

interface IPreferredServiceSupervisor {
    id?: string;
    serviceSupervisor: IServiceSupervisor;
    serviceSupervisorId: string;
}

interface IServiceGroupSettingsAddEditState {
    errors: ValidationErrors;

    group?: IGroup;
    isReadOnly: boolean;

    isSaving: boolean;
    overlap?: {
        endDate: string | null;
        id: string;
        startDate: string;
    };

    preferredServiceSupervisors: IPreferredServiceSupervisor[];
    selectedServiceSupervisors: SelectedServiceSupervisors;

    service?: IService;
    serviceGroupSettings?: IServiceGroupSettings;

    services: IService[];
}

class WrappedServiceGroupSettingsAddEdit extends Component<
    ServiceGroupSettingsAddEditProps,
    IServiceGroupSettingsAddEditState
> {
    private readonly atMostOncePerSecond = new RateLimiter(1000);
    private readonly overlapChecker = new OverlapChecker('/api/serviceGroupSettings');

    constructor(props: ServiceGroupSettingsAddEditProps, context?: unknown) {
        super(props, context);

        this.state = {
            errors: emptyValidationErrors,
            isReadOnly: !hasOneAuthorization([
                ManagerAuthorization.groupAddEdit,
                ManagerAuthorization.serviceRightsGroups,
            ]),
            isSaving: false,
            preferredServiceSupervisors: [],
            selectedServiceSupervisors: Map(),
            services: [],
        };
    }

    public async componentDidMount() {
        if (this.getQueryGroupId() && this.getQueryServiceId()) {
            throw Error('Cannot have both groupId and serviceId');
        }

        if (this.getQueryGroupId()) {
            await this.loadServices();
        } else if (!this.getQueryServiceId()) {
            throw Error('Missing either groupId or serviceId');
        }
        await this.setup(this.props.router.params.id);
    }

    public async componentDidUpdate(prevProps: Readonly<ServiceGroupSettingsAddEditProps>) {
        if (this.props.router.params.id !== prevProps.router.params.id) {
            await this.setup(this.props.router.params.id);
        }
    }

    public render() {
        const translate = getLanguage();
        const groupId = this.props.router.searchParams.getOptional('groupId');

        return (
            <FixedWidthTemplate breadcrumb={this.getBreadcrumb()}>
                <Header
                    content={
                        this.state.isReadOnly
                            ? translate.groupSettings.serviceGroupSettings
                            : (this.isEdit() ? translate.common.edit : translate.common.new) +
                              ' ' +
                              translate.groupSettings.serviceGroupSettings
                    }
                />

                <Loading
                    item={this.state.serviceGroupSettings}
                    loaded={(serviceGroupSettings) => (
                        <FormContainer onSubmit={async () => await this.save()}>
                            <div style={{ paddingTop: '30px' }}>
                                {this.state.overlap && (
                                    <OverlapMessage
                                        endDate={this.state.overlap.endDate}
                                        link={
                                            `/serviceGroupSettings/${this.state.overlap.id}?` +
                                            `${
                                                this.getQueryServiceId() && this.getStateServiceId()
                                                    ? `serviceId=${this.getStateServiceId()}`
                                                    : ''
                                            }` +
                                            `${
                                                this.getQueryGroupId() && this.getStateGroupId()
                                                    ? `groupId=${this.getStateGroupId()}`
                                                    : ''
                                            }`
                                        }
                                        message={translate.serviceConsumersGroupSettings.overlapMessage}
                                        startDate={this.state.overlap.startDate}
                                    />
                                )}
                                <FormGroup hasErrors={this.state.errors.has('startDate')}>
                                    <FormLabel isRequired>{translate.common.startDate}</FormLabel>
                                    <InputGroup>
                                        {this.state.isReadOnly ? (
                                            this.renderText(serviceGroupSettings.startDate)
                                        ) : (
                                            <DateInput
                                                error={this.state.errors.get('startDate')}
                                                onChange={async (value) => await this.handleChange('startDate', value)}
                                                onDoValidate={() => this.validateOne('startDate')}
                                                value={serviceGroupSettings.startDate}
                                            />
                                        )}
                                    </InputGroup>
                                </FormGroup>

                                <FormGroup hasErrors={this.state.errors.has('endDate')}>
                                    <FormLabel>{translate.common.endDate}</FormLabel>
                                    <InputGroup>
                                        {this.state.isReadOnly ? (
                                            this.renderText(serviceGroupSettings.endDate)
                                        ) : (
                                            <DateInput
                                                error={this.state.errors.get('endDate')}
                                                onChange={async (value) => await this.handleChange('endDate', value)}
                                                onDoValidate={() => this.validateOne('endDate')}
                                                value={serviceGroupSettings.endDate}
                                            />
                                        )}
                                    </InputGroup>
                                </FormGroup>

                                {this.getQueryGroupId() && !this.isEdit() && (
                                    <FormGroup>
                                        <FormLabel isRequired>{translate.service.service}</FormLabel>
                                        <InputGroup>
                                            <Dropdown
                                                error={this.state.errors.get('serviceId')}
                                                onChange={async (value) => {
                                                    await this.handleChange('serviceId', value);
                                                    await this.loadService();
                                                }}
                                                onDoValidate={() => this.validateOne('serviceId')}
                                                selectOptions={this.getServicesSelectOptions()}
                                                value={serviceGroupSettings.serviceId}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}

                                {this.getQueryServiceId() && !this.isEdit() && (
                                    <FormGroup>
                                        <FormLabel isRequired>{translate.groups.group}</FormLabel>
                                        <InputGroup>
                                            <DropdownSearch
                                                error={this.state.errors.get('groupId')}
                                                loadOptions={(input) => this.loadGroupSelectOptions(input)}
                                                onChange={async (value) => {
                                                    await this.handleChange('groupId', value);
                                                    await this.loadGroup();
                                                }}
                                                onDoValidate={async () => await this.validateOne('groupId')}
                                                value={serviceGroupSettings.groupId}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                                {this.serviceIsExamination() && (
                                    <FormGroup>
                                        <FormLabel>{translate.groupSettings.supervisionPercentage}</FormLabel>
                                        <InputContainer small>
                                            {this.state.isReadOnly ? (
                                                this.renderText(serviceGroupSettings.supervisionPercentage)
                                            ) : (
                                                <InputNumber
                                                    disabled={!this.canUpdateSupervisionPercentage()}
                                                    error={this.state.errors.get('supervisionPercentage')}
                                                    onChange={async (value) =>
                                                        await this.handleChange('supervisionPercentage', value)
                                                    }
                                                    onDoValidate={() => this.validateOne('supervisionPercentage')}
                                                    value={serviceGroupSettings.supervisionPercentage}
                                                />
                                            )}
                                        </InputContainer>
                                    </FormGroup>
                                )}

                                <FormGroup>
                                    <FormLabel>{translate.serviceConsumer.isKetenDbc}</FormLabel>
                                    <InputGroup>
                                        {this.state.isReadOnly ? (
                                            this.renderText(
                                                serviceGroupSettings.isKetenDbc
                                                    ? translate.common.yes
                                                    : translate.common.no
                                            )
                                        ) : (
                                            <InputCheckbox
                                                checked={serviceGroupSettings.isKetenDbc}
                                                error={this.state.errors.get('isKetenDbc')}
                                                onChange={async (value) => this.handleChange('isKetenDbc', value)}
                                                onDoValidate={async () => this.validateOne('isKetenDbc')}
                                            />
                                        )}
                                    </InputGroup>
                                </FormGroup>

                                {this.usesEmbloom() && this.state.serviceGroupSettings && (
                                    <>
                                        <FormGroup hasErrors={this.state.errors.has('embloomGroupCertificate')}>
                                            <FormLabel isRequired>
                                                {translate.embloomQuestionnaires.certificate}
                                            </FormLabel>
                                            <InputGroup>
                                                {this.state.isReadOnly ? (
                                                    this.renderText(serviceGroupSettings.embloomGroupCertificate)
                                                ) : (
                                                    <EnumDropdown
                                                        enumName={'embloomGroupCertificate'}
                                                        error={this.state.errors.get('embloomGroupCertificate')}
                                                        jsEnum={EmbloomGroupCertificate}
                                                        onChange={async (value) =>
                                                            await this.handleChange('embloomGroupCertificate', value)
                                                        }
                                                        onDoValidate={async () =>
                                                            await this.validateOne('embloomGroupCertificate')
                                                        }
                                                        value={serviceGroupSettings.embloomGroupCertificate}
                                                    />
                                                )}
                                            </InputGroup>
                                        </FormGroup>
                                    </>
                                )}

                                {this.state.service && this.state.service.code === ServiceCode.TFO && (
                                    <FormGroup>
                                        <FormLabel>{translate.groupSettings.noShowsImported}</FormLabel>
                                        {this.state.isReadOnly ? (
                                            this.renderText(
                                                serviceGroupSettings.noShowsImported
                                                    ? translate.common.yes
                                                    : translate.common.no
                                            )
                                        ) : (
                                            <InputGroup>
                                                <InputCheckbox
                                                    checked={serviceGroupSettings.noShowsImported}
                                                    error={this.state.errors.get('noShowsImported')}
                                                    onChange={async (value) =>
                                                        this.handleChange('noShowsImported', value)
                                                    }
                                                    onDoValidate={async () => this.validateOne('noShowsImported')}
                                                />
                                            </InputGroup>
                                        )}
                                    </FormGroup>
                                )}

                                {this.props.router.params.id && serviceGroupSettings.serviceId && (
                                    <FundingSettingsList
                                        groupId={groupId}
                                        serviceGroupSettingId={this.props.router.params.id}
                                        serviceId={serviceGroupSettings.serviceId}
                                    />
                                )}

                                {this.props.router.params.id && this.state.service && (
                                    <CompensationPayoutList
                                        groupId={groupId}
                                        serviceCode={this.state.service.code}
                                        serviceGroupSettingId={this.props.router.params.id}
                                        serviceId={this.state.service.id}
                                        serviceType={this.state.service.serviceType}
                                    />
                                )}

                                {this.serviceIsExamination() &&
                                    serviceGroupSettings.serviceId &&
                                    !this.state.isReadOnly && (
                                        <ServiceSupervisorListAddEdit
                                            onChange={async (value) =>
                                                await this.setState({ selectedServiceSupervisors: value })
                                            }
                                            selectedServiceSupervisors={this.state.selectedServiceSupervisors}
                                            serviceGroupSettingId={this.getParamServiceGroupSettingId()}
                                            serviceId={serviceGroupSettings.serviceId}
                                        />
                                    )}

                                {!this.state.isReadOnly && (
                                    <>
                                        <CancelButton
                                            disabled={this.state.isSaving}
                                            to={this.getReturnUrl()}
                                        />
                                        <Button
                                            automation="saveSubmit"
                                            color="secondary"
                                            disabled={this.state.isSaving || !!this.state.overlap}
                                            large
                                            submit
                                        >
                                            {translate.common.save}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async setup(serviceGroupSettingId: string | undefined) {
        if (serviceGroupSettingId) {
            await this.loadServiceGroupSetting(serviceGroupSettingId);

            if (!this.state.isReadOnly) {
                await this.loadPreferredServiceSupervisors(serviceGroupSettingId);
            }
        } else {
            await this.setState({
                preferredServiceSupervisors: [],
                selectedServiceSupervisors: Map<string, IServiceSupervisor>(),
                serviceGroupSettings: {
                    embloomGroupCertificate: null,
                    endDate: null,
                    groupId: this.getQueryGroupId(),
                    isKetenDbc: false,
                    noShowsImported: false,
                    serviceId: this.getQueryServiceId(),
                    startDate: DateTime.now().format('YYYY-MM-DD'),
                    supervisionPercentage: 0,
                },
            });
        }

        await this.loadService();
        await this.loadGroup();

        await this.setState({ overlap: undefined });
        await this.checkOverlap();
    }

    private getQueryGroupId() {
        return this.props.router.searchParams.get('groupId');
    }

    private getStateGroupId() {
        return this.state.serviceGroupSettings && this.state.serviceGroupSettings.groupId;
    }

    private getQueryServiceId() {
        return this.props.router.searchParams.get('serviceId');
    }

    private getStateServiceId() {
        return this.state.serviceGroupSettings && this.state.serviceGroupSettings.serviceId;
    }

    private canUpdateSupervisionPercentage() {
        return (
            this.state.service &&
            this.state.service.authorization &&
            this.state.service.authorization.canUpdateSupervisionPercentage
        );
    }

    private isEdit() {
        return !!this.getParamServiceGroupSettingId() || this.state.isReadOnly;
    }

    private getParamServiceGroupSettingId() {
        return this.props.router.params.id;
    }

    private serviceIsExamination() {
        return this.state.service && this.state.service.serviceType === ServiceType.examination;
    }

    private usesEmbloom() {
        return this.state.service && this.state.service.code === ServiceCode.GGZOembl;
    }

    private async handleChange<K extends keyof IServiceGroupSettings>(name: K, value: IServiceGroupSettings[K]) {
        await this.setState(({ serviceGroupSettings }) => ({
            serviceGroupSettings: { ...serviceGroupSettings, [name]: value },
        }));

        if (name === 'groupId' || name === 'serviceId' || name === 'startDate' || name === 'endDate') {
            this.checkOverlap();
        }
    }

    private async validateOne(name: keyof IServiceGroupSettings) {
        await this.setState(({ serviceGroupSettings, errors }) => ({
            errors: updateValidationErrorsForOne(this.getSchema(), serviceGroupSettings, errors, name),
        }));
    }

    private async checkOverlap() {
        const serviceId = this.getStateServiceId();
        const groupId = this.getStateGroupId();
        if (serviceId && groupId) {
            this.atMostOncePerSecond.callMeMaybe(async () => {
                if (this.state.serviceGroupSettings) {
                    const overlap = await this.overlapChecker.check(
                        { groupId: groupId, serviceId: serviceId },
                        this.state.serviceGroupSettings.startDate,
                        this.state.serviceGroupSettings.endDate,
                        this.getParamServiceGroupSettingId()
                    );
                    await this.setState({ overlap });
                }
            });
        }
    }

    private async loadServiceGroupSetting(serviceGroupSettingId: string) {
        const url = `/api/serviceGroupSettings/${serviceGroupSettingId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ serviceGroupSettings: result.json });
    }

    private async loadGroup() {
        const groupId = this.getStateGroupId();
        if (groupId) {
            const result = await fetchJson(`/api/groups/${groupId}`, 'GET');
            await this.setState({ group: result.json });
        } else {
            await this.setState({ group: undefined });
        }
    }

    private async loadGroupSelectOptions(input: string) {
        const result = await fetchJson(`/api/groups?searchText=${input}`, 'GET');
        const options = result.json.items.map((group: IGroup) => ({
            label: group.name,
            value: group.id,
        }));

        return options;
    }

    private async loadService() {
        const serviceId = this.getStateServiceId();
        const autorizationQuery = 'includeItemAuthorizations=canUpdateSupervisionPercentage';
        if (serviceId) {
            const result = await fetchJson(`/api/services/${serviceId}?${autorizationQuery}`, 'GET');
            await this.setState({ service: result.json });
        } else {
            await this.setState({ service: undefined });
        }
    }

    private async loadServices() {
        const url = '/api/services?selectionList=true';
        const results = await fetchJson(url, 'GET');
        await this.setState({ services: results.json.items });
    }

    private getServicesSelectOptions() {
        const translate = getLanguage();
        const options = [{ text: formatString(translate.common.selectItem, [translate.service.service]), value: '' }];

        return options.concat(
            this.state.services.map((item) => {
                return { text: item.name, value: item.id };
            })
        );
    }

    private async save() {
        if (this.state.overlap) {
            return;
        }

        const { errors, item } = validate(this.getSchema(), this.state.serviceGroupSettings);
        await this.setState({ errors });
        if (errors.size > 0) {
            return false;
        }

        let url = '/api/serviceGroupSettings';
        if (this.isEdit()) {
            url += `/${this.getParamServiceGroupSettingId()}`;
        }
        await this.setState({ isSaving: true });

        const result = await fetchJson(url, 'POST', item);
        const serviceGroupSettingId = result.json.id || this.getParamServiceGroupSettingId();

        this.state.selectedServiceSupervisors.valueSeq().forEach(async (selectedServiceSupervisor) => {
            if (
                selectedServiceSupervisor &&
                !this.state.preferredServiceSupervisors.find(
                    (prefServiceSuper) => prefServiceSuper.serviceSupervisorId === selectedServiceSupervisor.id
                )
            ) {
                await fetchJson('/api/preferredServiceSupervisors', 'POST', {
                    serviceGroupSettingId,
                    serviceSupervisorId: selectedServiceSupervisor.id,
                });
            }
        });
        for (const preferredServiceSupervisor of this.state.preferredServiceSupervisors) {
            if (!this.state.selectedServiceSupervisors.has(preferredServiceSupervisor.serviceSupervisorId)) {
                await fetchJson(`/api/preferredServiceSupervisors/${preferredServiceSupervisor.id}`, 'DELETE');
            }
        }

        this.props.router.navigate(this.getReturnUrl());
    }

    private getReturnUrl() {
        if (this.getQueryGroupId()) {
            return `/groups/${this.getQueryGroupId()}`;
        } else if (this.getQueryServiceId()) {
            return `/services/${this.getQueryServiceId()}?tab=groupSettings`;
        }
        throw Error('Unknown return destination');
    }

    private async loadPreferredServiceSupervisors(serviceGroupSettingId: string) {
        const url = `/api/preferredServiceSupervisors?serviceGroupSettingsId=${serviceGroupSettingId}&isActive=true`;
        const results = await fetchJson(url, 'GET');
        let selectedServiceSupervisors: SelectedServiceSupervisors = Map();
        results.json.items.forEach((item: IPreferredServiceSupervisor) => {
            selectedServiceSupervisors = selectedServiceSupervisors.set(
                item.serviceSupervisorId,
                item.serviceSupervisor
            );
        });
        await this.setState({ preferredServiceSupervisors: results.json.items, selectedServiceSupervisors });
    }

    private getSchema() {
        return this.isEdit() ? serviceGroupSettingsEditSchema : serviceGroupSettingsAddSchema;
    }

    private getBreadcrumb() {
        const breadcrumbs: IBreadcrumbItem[] = [];
        const translate = getLanguage();

        if (this.isEdit()) {
            if (this.state.group && this.state.service) {
                if (this.getQueryGroupId()) {
                    breadcrumbs.push({ text: this.state.group.name, url: this.getReturnUrl() });
                    breadcrumbs.push({ text: this.state.service.name, url: this.getReturnUrl() });
                } else if (this.getQueryServiceId()) {
                    breadcrumbs.push({ text: this.state.service.name, url: this.getReturnUrl() });
                    breadcrumbs.push({ text: this.state.group.name, url: this.getReturnUrl() });
                }
            }
            breadcrumbs.push({
                text: translate.common.edit + ' ' + translate.groupSettings.serviceGroupSettings,
            });
        } else {
            if (this.getQueryGroupId() && this.state.group) {
                breadcrumbs.push({ text: this.state.group.name, url: this.getReturnUrl() });
            } else if (this.getQueryServiceId() && this.state.service) {
                breadcrumbs.push({ text: this.state.service.name, url: this.getReturnUrl() });
            }
            breadcrumbs.push({
                text: translate.common.new + ' ' + translate.groupSettings.serviceGroupSettings,
            });
        }

        return breadcrumbs;
    }

    private renderText(content: string | number | null) {
        return (
            <Text
                as="p"
                fontWeight="medium"
            >
                {content}
            </Text>
        );
    }
}

export const ServiceGroupSettingsAddEdit = withRouter(WrappedServiceGroupSettingsAddEdit);
